import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Cart from './Cart';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_07dd2ee2-42aa-4d8a-a966-79b38cca8e94/artifacts/skr2sta7_Logo.png";

const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'MUSIC', path: '/music' },
    { name: 'MERCH', path: '/merch' },
    { name: 'TOUR', path: '/tour' },
];

export default function Navbar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { isCartOpen, setIsCartOpen, getCartCount } = useCart();
    const location = useLocation();
    const cartCount = getCartCount();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    return (
        <nav 
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                isScrolled ? 'glass-strong' : 'bg-transparent'
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
                        <img 
                            src={LOGO_URL} 
                            alt="StillFaded" 
                            className="h-12 w-auto"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-12">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                data-testid={`nav-link-${link.name.toLowerCase()}`}
                                className={`font-space text-sm tracking-widest transition-colors duration-300 ${
                                    location.pathname === link.path 
                                        ? 'text-white' 
                                        : 'text-slate-400 hover:text-white'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* Cart & Mobile Menu */}
                    <div className="flex items-center gap-4">
                        {/* Cart Button */}
                        <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
                            <SheetTrigger asChild>
                                <button 
                                    data-testid="cart-button"
                                    className="relative p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors duration-300"
                                >
                                    <ShoppingBag className="w-5 h-5 text-white" />
                                    {cartCount > 0 && (
                                        <Badge 
                                            data-testid="cart-count"
                                            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-white text-black"
                                        >
                                            {cartCount}
                                        </Badge>
                                    )}
                                </button>
                            </SheetTrigger>
                            <SheetContent 
                                side="right" 
                                className="w-full sm:max-w-lg bg-brand-surface border-brand-border"
                            >
                                <SheetHeader>
                                    <SheetTitle className="font-syne text-2xl tracking-tight text-white">YOUR CART</SheetTitle>
                                </SheetHeader>
                                <Cart />
                            </SheetContent>
                        </Sheet>

                        {/* Mobile Menu Button */}
                        <button 
                            data-testid="mobile-menu-button"
                            className="md:hidden p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors duration-300"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            {isMobileMenuOpen ? (
                                <X className="w-5 h-5 text-white" />
                            ) : (
                                <Menu className="w-5 h-5 text-white" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div 
                    data-testid="mobile-menu"
                    className="md:hidden glass-strong animate-fadeIn"
                >
                    <div className="px-6 py-8 space-y-6">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                                className={`block font-syne text-2xl tracking-tight ${
                                    location.pathname === link.path 
                                        ? 'text-white' 
                                        : 'text-slate-400'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </nav>
    );
}
